/**
 * CommandPalette — Cmd+K / Ctrl+K globale zoek.
 *
 * Combineert navigatie-items (role-gefilterd) met live data-resultaten uit
 * /api/search/global (klanten, offertes, orders, facturen, opportunities,
 * wasserij-items). cmdk's eigen filter staat uit: server-resultaten bevatten
 * de zoekterm vaak niet letterlijk in de getoonde tekst (zoek op postcode →
 * toont naam), dus filteren we navigatie zelf en tonen we data ongefilterd.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { useUserRole } from '../../hooks/useUserRole';
import { useGlobalSearch, type GlobalResultType } from '../../hooks/useGlobalSearch';
import { filterNavigationByRole } from './navigation';
import {
  KlantenIcon,
  OfferteIcon,
  OrderIcon,
  FacturenIcon,
  OpportunitiesIcon,
  PackageIcon,
} from './sidebar-icons';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

const RESULT_GROUPS: ReadonlyArray<{
  key: 'customers' | 'contacts' | 'offertes' | 'orders' | 'invoices' | 'opportunities' | 'items';
  heading: string;
  Icon: () => JSX.Element;
}> = [
  { key: 'customers', heading: 'Klanten', Icon: KlantenIcon },
  { key: 'contacts', heading: 'Contactpersonen', Icon: KlantenIcon },
  { key: 'offertes', heading: 'Offertes', Icon: OfferteIcon },
  { key: 'orders', heading: 'Orders', Icon: OrderIcon },
  { key: 'invoices', heading: 'Facturen', Icon: FacturenIcon },
  { key: 'opportunities', heading: 'Opportunities', Icon: OpportunitiesIcon },
  { key: 'items', heading: 'Wasserij-items', Icon: PackageIcon },
];

// Contact navigeert naar de klantpagina met de contact-drawer direct open.
const ROUTE_BY_TYPE: Record<GlobalResultType, (result: { id: string; accountId?: string }) => string> = {
  customer: (r) => `/klanten/${r.id}`,
  contact: (r) => (r.accountId ? `/klanten/${r.accountId}?contact=${r.id}` : `/klanten/${r.id}`),
  offerte: (r) => `/offertes/${r.id}`,
  order: (r) => `/orders/${r.id}`,
  invoice: (r) => `/facturen/${r.id}`,
  opportunity: (r) => `/opportunities/${r.id}`,
  item: (r) => `/items/${r.id}`,
};

const GROUP_HEADING_CLASS =
  'px-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-slate-500';

const ITEM_CLASS =
  'flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-700 cursor-pointer data-[selected=true]:bg-indigo-50 data-[selected=true]:text-indigo-700 aria-selected:bg-indigo-50 aria-selected:text-indigo-700';

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { role } = useUserRole();
  const [query, setQuery] = useState('');

  const { groups, isLoading } = useGlobalSearch(query, open);

  // Query resetten zodra de palette sluit, zodat heropenen schoon start.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const navSections = useMemo(() => {
    if (!role) return [];
    const all = filterNavigationByRole(role);
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            item.name.toLowerCase().includes(q) ||
            (section.label ?? '').toLowerCase().includes(q)
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [role, query]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onOpenChange(false);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  if (!open) return null;

  const hasDataResults = RESULT_GROUPS.some(({ key }) => groups[key].length > 0);
  const hasNavResults = navSections.length > 0;
  const showEmpty =
    query.trim().length >= 2 && !isLoading && !hasDataResults && !hasNavResults;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] bg-black/40"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="w-full max-w-xl mx-4 bg-white rounded-xl shadow-2xl overflow-hidden border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <Command label="Globaal zoeken" shouldFilter={false}>
          <div className="flex items-center px-4 border-b border-slate-200">
            <svg
              className="w-4 h-4 text-slate-400 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
              />
            </svg>
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder="Zoek klant, order, offerte, factuur..."
              className="flex-1 px-3 py-3 text-sm bg-transparent outline-none placeholder:text-slate-400"
            />
          </div>
          <Command.List className="max-h-[60vh] overflow-y-auto py-2">
            {showEmpty && (
              <div className="px-4 py-6 text-sm text-slate-500 text-center">
                Geen resultaten voor &lsquo;{query.trim()}&rsquo;.
              </div>
            )}
            {navSections.map((section) => (
              <Command.Group
                key={section.id}
                heading={section.label ?? 'Werkruimte'}
                className={GROUP_HEADING_CLASS}
              >
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Command.Item
                      key={item.id}
                      value={`nav-${item.id}`}
                      onSelect={() => {
                        navigate(item.href);
                        onOpenChange(false);
                      }}
                      className={ITEM_CLASS}
                    >
                      <span className="text-slate-500 [&_svg]:w-4 [&_svg]:h-4">
                        <Icon />
                      </span>
                      <span className="flex-1">{item.name}</span>
                      <span className="text-xs text-slate-400">{item.href}</span>
                    </Command.Item>
                  );
                })}
              </Command.Group>
            ))}
            {RESULT_GROUPS.map(({ key, heading, Icon }) => {
              const results = groups[key];
              if (results.length === 0) return null;
              return (
                <Command.Group key={key} heading={heading} className={GROUP_HEADING_CLASS}>
                  {results.map((result) => (
                    <Command.Item
                      key={`${result.type}-${result.id}`}
                      value={`${result.type}-${result.id}`}
                      onSelect={() => {
                        navigate(ROUTE_BY_TYPE[result.type](result));
                        onOpenChange(false);
                      }}
                      className={ITEM_CLASS}
                    >
                      <span className="text-slate-500 [&_svg]:w-4 [&_svg]:h-4">
                        <Icon />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-2">
                          <span className="truncate font-medium">{result.title}</span>
                          {result.badge === 'blacklist' && (
                            <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-700">
                              Blacklist
                            </span>
                          )}
                        </span>
                        {result.subtitle && (
                          <span className="block truncate text-xs text-slate-400">
                            {result.subtitle}
                          </span>
                        )}
                      </span>
                      {result.meta && (
                        <span className="flex-shrink-0 text-xs text-slate-400 max-w-[40%] truncate">
                          {result.meta}
                        </span>
                      )}
                    </Command.Item>
                  ))}
                </Command.Group>
              );
            })}
          </Command.List>
          <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200 text-xs text-slate-400 bg-slate-50">
            <span>{isLoading ? 'Zoeken…' : '↑↓ navigeren · ↵ openen · esc sluiten'}</span>
            <span className="font-mono">⌘K</span>
          </div>
        </Command>
      </div>
    </div>
  );
}
